import React, { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { getGuideExperiences } from "store/guide-profile"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import CommonEmptyStateUI from "../CommonEmptyStateUI"
import { Title } from "ui/Typography"
import { useTranslation } from "react-i18next"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useIsMobile from "./hooks/useIsMobile"
import DatePickerFilter from "./components/DatePickerFilter"
import ExperienceGrid from "./components/ExperienceGrid"
import ExperienceCarousel from "./components/ExperienceCarousel"
import styles from "./index.module.css"
const PER_PAGE = 12

const GuideExperiences = () => {
  const dispatch = useDispatch()
  const { slug } = useParams()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [scrollContainer, setScrollContainer] = useState(null)
  const scrollContainerRef = useCallback((node) => {
    if (node) {
      setScrollContainer(node)
    }
  }, [])

  const experiences = useSelector((store) => store?.guideProfile?.experiences?.data)
  const { guide } = useSelector((store) => store?.user)

  const [selectedDate, setSelectedDate] = useState(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isPaginationLoading, setIsPaginationLoading] = useState(false)

  useEffect(() => {
    if (slug) {
      setPage(1)
      setHasMore(true)
      dispatch(getGuideExperiences(slug, null, 1, PER_PAGE))
    }
  }, [slug, dispatch])

  const soldOutDates = experiences?.availability?.sold_out_dates?.map((d) => d.date) || []

  const handleDateChange = useCallback((date) => {
    const formattedDate = date ? date.format("YYYY-MM-DD") : null
    setSelectedDate(formattedDate)
    setPage(1)
    setHasMore(true)
    dispatch(getGuideExperiences(slug, formattedDate, 1, PER_PAGE))
  }, [slug, dispatch])

  const clearDate = useCallback(() => {
    setSelectedDate(null)
    setPage(1)
    setHasMore(true)
    dispatch(getGuideExperiences(slug, null, 1, PER_PAGE))
  }, [slug, dispatch])

  const handleLoadMore = useCallback(async () => {
    if (!isPaginationLoading && hasMore) {
      setIsPaginationLoading(true)
      const nextPage = page + 1

      try {
        await dispatch(getGuideExperiences(slug, selectedDate, nextPage, PER_PAGE))
        setPage(nextPage)
      } catch (error) {
        console.error(error)
        setHasMore(false)
      } finally {
        setIsPaginationLoading(false)
      }
    }
  }, [isPaginationLoading, hasMore, page, slug, selectedDate, dispatch])

  const hasExperiences = Array.isArray(experiences?.trips) && experiences?.trips?.length > 0

  return (
    <FirstLoading name="guideProfile.experiences">
      <div className={styles.guideExperiencesContainer}>
        {hasExperiences && (
          <div className={styles.experiencesHeader}>
            <Title level={4}>
              {t("guide.navigation.this_guide_proposes_experiences", {
                count: experiences?.metadata?.total_results || 0
              })}
            </Title>
            <DatePickerFilter
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              onClear={clearDate}
              soldOutDates={soldOutDates}
            />
          </div>
        )}

        <div className={styles.experiencesList}>
          <WithEmptyStateWrapper
            name="experiences"
            hasItems={hasExperiences}
            fallback={
              <CommonEmptyStateUI
                titleKey="guide.navigation.no_experiences"
                descKey="guide.navigation.experiences_will_be_displayed_here"
              />
            }
          >
            <div ref={scrollContainerRef} className={styles.scrollableContainer}>
              {isMobile ? (
                <ExperienceCarousel
                  experiences={experiences?.trips}
                  guuid={guide?.uuid}
                  loading={isPaginationLoading}
                  hasMore={hasMore}
                  onLoadMore={handleLoadMore}
                  metadata={experiences?.metadata}
                  scrollableAncestor={scrollContainer}
                />
              ) : (
                <ExperienceGrid
                  experiences={experiences?.trips}
                  guuid={guide?.uuid}
                  loading={isPaginationLoading}
                  hasMore={hasMore}
                  onLoadMore={handleLoadMore}
                  metadata={experiences?.metadata}
                  scrollableAncestor={scrollContainer}
                />
              )}
            </div>
          </WithEmptyStateWrapper>
        </div>
      </div>
    </FirstLoading>
  )
}

export default GuideExperiences
